import { useTranslation } from 'react-i18next';

import { getLocaleSufix } from 'helpers/getLocaleSufix';
import { useChangeLanguage } from 'hooks/useLocaleNavigate';
import { DEFAULT_LANGUAGE, type SupportedLanguage } from 'i18n';

import { useGetUserCountry } from 'use-cases/get-user-country';

import { Analytics } from 'services/analytics';

import type { DropDownList } from 'utils/links';
import { getConversionLinks, getLinksAdvancedTools, getLinksTools } from 'utils/links';

import { BASE_URL, PAGE_LINKS } from 'ts/constants/page-links';
import type { InternalFileType } from 'ts/types/file';
import { InternalType } from 'ts/types/file';

export interface IFooterInteractor {
  links: {
    from: DropDownList;
    tools: DropDownList;
    advancedTools: DropDownList;
  };
  navigationLinks: {
    aboutUs: {
      href: string;
      text: string;
      onClick: () => void;
    };
    contactUs: {
      href: string;
      text: string;
      onClick: () => void;
    };
    blog: {
      href: string;
      text: string;
      onClick: () => void;
    };
    faq: {
      href: string;
      text: string;
      onClick: () => void;
    };
  };
  copyright: string;
  subscription: {
    href: string;
    text: string;
    onClick: () => void;
  };
  terms: {
    href: string;
    text: string;
    onClick: () => void;
  };
  privacy: {
    text: string;
    href: string;
    onClick: () => void;
  };
  notice: {
    text: string;
    href: string;
    onClick: () => void;
  };
  moneyback: {
    text: string;
    href: string;
    onClick: () => void;
  };
  cookie: {
    text: string;
    href: string;
    onClick: () => void;
  };
  faqForEu: {
    text: string;
    href: string;
    onClick: () => void;
  };
  linkedIn: {
    text: string;
    href: string;
    onClick: () => void;
  };
  facebook: {
    text: string;
    href: string;
    onClick: () => void;
  };
  twitter: {
    text: string;
    href: string;
    onClick: () => void;
  };
  pinterest: {
    text: string;
    href: string;
    onClick: () => void;
  };
  tools: {
    pdf_to_word: {
      href: string;
      text: string;
      onClick: () => void;
    };
    edit: {
      href: string;
      text: string;
      onClick: () => void;
    };
    compress: {
      href: string;
      text: string;
      onClick: () => void;
    };
    merge_pdf: {
      href: string;
      text: string;
      onClick: () => void;
    };
    sign_pdf: {
      href: string;
      text: string;
      onClick: () => void;
    };
    pdf_converter: {
      href: string;
      text: string;
      onClick: () => void;
    };
    pdf_to_excel: {
      href: string;
      text: string;
      onClick: () => void;
    };
    pdf_to_pptx: {
      href: string;
      text: string;
      onClick: () => void;
    };
  };
  clickOnLogo: () => void;
  clickOnFeature: (from: InternalFileType, label?: string, to?: InternalFileType) => void;
  clickOnChangeLang: (language: SupportedLanguage) => void;
  userLanguage: SupportedLanguage;

  isUSA: boolean;
  isEurope: boolean;
  isUAE: boolean;
}

export const useFooterInteractor = (): IFooterInteractor => {
  const { t, i18n } = useTranslation();
  const changeLanguage = useChangeLanguage();

  const { isUSA, isEurope, isUAE } = useGetUserCountry();

  const handleClickOnLogo = () => {
    void Analytics.sendEvent({
      event: 'main_page_tap'
    });
  };

  const clickOnFeature = (from: InternalFileType, label?: string, to?: InternalFileType) => {
    const fromTitle = InternalType.toTitle(from).toLowerCase();
    const toTitle = to ? InternalType.toTitle(to).toLowerCase() : '';
    const featuresName = () => {
      if (to) {
        return `${fromTitle}_to_${toTitle}`;
      }

      if (label?.toLowerCase().includes('form')) {
        return label?.replace('-', '_').replace(' ', '_').toLowerCase();
      }
      return 'edit_pdf';
    };

    void Analytics.sendEvent({
      event: 'footer_features_tap',
      data: { features_name: featuresName() }
    });
  };

  const clickOnSubscription = () => {
    void Analytics.sendEvent({
      event: 'subscription_terms_tap'
    });
  };

  const clickOnTerms = () => {
    void Analytics.sendEvent({
      event: 'terms_and_conds_tap'
    });
  };

  const clickOnPrivacy = () => {
    void Analytics.sendEvent({
      event: 'privacy_policy_tap'
    });
  };

  const clickOnNotice = () => {
    void Analytics.sendEvent({
      event: 'notice_tap'
    });
  };

  const clickOnMoneyBack = () => {
    void Analytics.sendEvent({
      event: 'money_back_policy_tap'
    });
  };

  const clickOnCookie = () => {
    Analytics.sendEvent({
      event: 'cookie_policy_tap'
    });
  };
  const clickOnFaq = () => {
    Analytics.sendEvent({
      event: 'faq_eu_residents_tap'
    });
  };

  const clickOnAboutUs = () => {
    void Analytics.sendEvent({
      event: 'about_us_tap'
    });
  };

  const clickOnContactUs = () => {
    void Analytics.sendEvent({
      event: 'contact_us_tap'
    });
  };

  const clickOnBlog = () => {
    void Analytics.sendEvent({
      event: 'blog_tap'
    });
  };

  const clickOnTools = (toolName: string) => {
    void Analytics.sendEvent({
      event: `${toolName}_tap`
    });
  };

  const clickOnFaqForEu = () => {
    Analytics.sendEvent({
      event: 'faq_eu_residents_tap'
    });
  };

  const clickOnSocial = (socialMediaName: string) => {
    void Analytics.sendEvent({
      event: `${socialMediaName}_tap`
    });
  };

  const clickOnChangeLang = (locale: SupportedLanguage) => {
    Analytics.sendEvent({
      event: 'change_language_tap',
      data: {
        type: locale
      }
    });
    changeLanguage(locale);
  };

  return {
    links: {
      from: getConversionLinks(t),
      tools: getLinksTools(t),
      advancedTools: getLinksAdvancedTools(t)
    },
    navigationLinks: {
      aboutUs: {
        href: PAGE_LINKS.ABOUT_US,
        text: t('footer.about_us'),
        onClick: clickOnAboutUs
      },
      contactUs: {
        href: PAGE_LINKS.CONTACT_US,
        text: t('footer.contact_us'),
        onClick: clickOnContactUs
      },
      blog: {
        href: PAGE_LINKS.BLOG,
        text: t('footer.blog'),
        onClick: clickOnBlog
      },
      faq: {
        href: PAGE_LINKS.FAQ,
        text: t('footer.faq'),
        onClick: clickOnFaq
      }
    },
    copyright: isUSA
      ? t('footer.copyright_usa', { date: new Date().getFullYear() })
      : t('footer.copyright', { date: new Date().getFullYear() }),
    subscription: {
      href: `${BASE_URL}${getLocaleSufix(i18n.language)}${PAGE_LINKS.SEO_LINKS.SUBSCRIPTION_TERMS}`,
      text: t('footer.subscription_terms'),
      onClick: clickOnSubscription
    },
    terms: {
      href: `${BASE_URL}${getLocaleSufix(i18n.language)}${PAGE_LINKS.SEO_LINKS.TERMS}`,
      text: t('footer.terms_and_conditions'),
      onClick: clickOnTerms
    },
    privacy: {
      href: `${BASE_URL}${getLocaleSufix(i18n.language)}${PAGE_LINKS.SEO_LINKS.PRIVACY_POLICY}`,
      text: t('footer.privacy_policy'),
      onClick: clickOnPrivacy
    },
    notice: {
      href: `${BASE_URL}${getLocaleSufix(i18n.language)}${PAGE_LINKS.SEO_LINKS.NOTICE}`,
      text: t('footer.notice'),
      onClick: clickOnNotice
    },
    moneyback: {
      href: `${BASE_URL}${getLocaleSufix(i18n.language)}${PAGE_LINKS.SEO_LINKS.MONEY_BACK_POLICY}`,
      text: t('footer.money_back'),
      onClick: clickOnMoneyBack
    },
    cookie: {
      href: `${BASE_URL}${getLocaleSufix(i18n.language)}${PAGE_LINKS.SEO_LINKS.COOKIE_POLICY}`,
      text: t('footer.cookie_policy'),
      onClick: clickOnCookie
    },
    faqForEu: {
      href: `${BASE_URL}${getLocaleSufix(i18n.language)}${PAGE_LINKS.SEO_LINKS.FAQ_EU}`,
      text: t('footer.faq_eu'),
      onClick: clickOnFaqForEu
    },
    linkedIn: {
      href: PAGE_LINKS.SOCIAL_LinkedIn,
      text: 'LinkedIn',
      onClick: () => clickOnSocial('linkedIn')
    },
    facebook: {
      href: PAGE_LINKS.SOCIAL_Facebook,
      text: 'Facebook',
      onClick: () => clickOnSocial('facebook')
    },
    twitter: {
      href: PAGE_LINKS.SOCIAL_Twitter,
      text: 'X',
      onClick: () => clickOnSocial('x')
    },
    pinterest: {
      href: PAGE_LINKS.SOCIAL_Pinterest,
      text: 'Pinterest',
      onClick: () => clickOnSocial('pinterest')
    },
    tools: {
      pdf_to_word: {
        href: PAGE_LINKS.PDF_TO_WORD,
        text: t('footer.pdf_to_word'),
        onClick: () => clickOnTools('pdf_to_word')
      },
      edit: {
        href: PAGE_LINKS.EDIT_PDF,
        text: t('footer.edit_pdf'),
        onClick: () => clickOnTools('edit_pdf')
      },
      compress: {
        href: PAGE_LINKS.COMPRESS_PDF,
        text: t('footer.compress'),
        onClick: () => clickOnTools('compress')
      },
      merge_pdf: {
        href: PAGE_LINKS.MERGE_PDF,
        text: t('footer.merge_pdf'),
        onClick: () => clickOnTools('merge_pdf')
      },
      pdf_converter: {
        href: PAGE_LINKS.PDF_CONVERTER,
        text: t('footer.pdf_converter'),
        onClick: () => clickOnTools('pdf_converter')
      },
      sign_pdf: {
        href: PAGE_LINKS.SIGNATURE_PDF,
        text: t('footer.sign_pdf'),
        onClick: () => clickOnTools('sign_pdf')
      },
      pdf_to_excel: {
        href: PAGE_LINKS.PDF_TO_EXCEL,
        text: t('footer.pdf_to_excel'),
        onClick: () => clickOnTools('pdf_to_excel')
      },
      pdf_to_pptx: {
        href: PAGE_LINKS.PDF_TO_PPTX,
        text: t('footer.pdf_to_pptx'),
        onClick: () => clickOnTools('pdf_to_pptx')
      }
    },
    clickOnLogo: handleClickOnLogo,
    clickOnFeature,
    clickOnChangeLang,
    userLanguage: (i18n.language || DEFAULT_LANGUAGE) as SupportedLanguage,

    isUSA,
    isEurope,
    isUAE
  };
};
